import { getActiveLocale } from '../i18n/i18n';

const sources = {
  gold: '/brand/varza-logo-yellow.svg',
  white: '/brand/varza-logo-white.svg',
  brown: '/brand/varza-logo-brown.svg',
} as const;

/** Which artwork: the gold mark, or the same mark struck in another ink for a coloured ground. */
export type BrandTone = keyof typeof sources;

/**
 * The Varza mark. It is gold on every surface and in either theme: the artwork
 * carries no backing shape, so it sits on the light and dark workbench greys
 * alike, and the brand stays one colour wherever it appears.
 *
 * The exceptions are marks drawn *on* a brand colour rather than on a surface — the
 * key at the foot of the phone shell is gold, and a gold bull on it would be
 * invisible. That is what `tone` is for, and it is the only reason to pass it: each
 * artwork is the same mark with its shapes struck in another ink, not a different
 * mark, so the three are interchangeable and none of them is a second logo.
 *
 * `brown` is the ink for gold, and it replaced the white one on that key. White on
 * gold is two light colours a hair apart: legible under a lamp, and gone the moment
 * a phone dims itself or the sun is on the glass. The deep brown is the gold's own
 * shadow taken all the way down, so the bull reads as struck into the disc rather
 * than laid on top of it, and it survives a panel at half brightness outdoors.
 */
export function BrandLogo({
  className = 'brand__logo',
  tone = 'gold',
}: {
  className?: string;
  tone?: BrandTone;
}) {
  const locale = getActiveLocale();
  const label = locale === 'fa' ? 'ورزا' : 'Varza';

  return (
    <img
      className={className}
      src={sources[tone]}
      alt={label}
      width={40}
      height={40}
      draggable={false}
    />
  );
}
